import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Loader, { ListLoader } from '../../utils/loader';
import api from '../../libs/axios';
import toast from 'react-hot-toast';
import Breadcrumb from '../../utils/breadcrumbs';
import Pagination from '../../utils/pagination2';
import ConfirmationModal from '../../ui/confirmation-modal';
import { FiUserPlus, FiEye, FiTrash2, FiEdit3 } from 'react-icons/fi';
import TeamOffcanvas from '../../components/teams/team-form-offcanvas';

interface IPlayer {
    id: number;
    player_name: string;
    player_role: number;
    jersey_number: number;
    is_captain: boolean;
    is_wicket_keeper: boolean;
}

export interface ITeam {
    team_initials: string;
    id: number;
    name: string;
    logo_url: string | null;
    location: string;
    owner_id: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    players_count: number;
}

interface IPagination {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

const Teams: React.FC = () => {
    const navigate = useNavigate();
    const [selectedTeam, setSelectedTeam] = useState<ITeam | null>(null);
    const [searchParams, setSearchParams] = useState<Record<string, string>>({});
    const [showTeamOffcanvas, setShowTeamOffcanvas] = useState(false);
    const [offcanvasMode, setOffcanvasMode] = useState<'create' | 'edit'>('create');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [teams, setTeams] = useState<ITeam[]>([]);
    const [pagination, setPagination] = useState<IPagination>({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null
    });
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [limit, setLimit] = useState(10);

    // Filter teams based on search parameters
    const filteredTeams = useMemo(() => {
        return teams.filter(team => {
            const nameMatch = !searchParams.name ||
                team.name.toLowerCase().includes(searchParams.name.toLowerCase());

            const locationMatch = !searchParams.location ||
                team.location.toLowerCase().includes(searchParams.location.toLowerCase());

            const statusMatch = !searchParams.is_active ||
                team.is_active.toString() === searchParams.is_active;

            return nameMatch && locationMatch && statusMatch;
        });
    }, [teams, searchParams]);

    // Fetch teams with search params
    const fetchTeams = async (useSearchParams = false) => {
        setLoading(true);
        try {
            const params: any = {
                page: pagination.current_page,
                limit: limit
            };

            // Add search parameter to API call if search params exist and useSearchParams is true
            if (useSearchParams && Object.keys(searchParams).length > 0) {
                // Combine all search fields into a single search string
                const searchTerms = [];
                if (searchParams.name) searchTerms.push(searchParams.name);
                if (searchParams.location) searchTerms.push(searchParams.location);

                if (searchTerms.length > 0) {
                    params.search = searchTerms.join(' ');
                }

                // Keep is_active as separate parameter
                if (searchParams.is_active) {
                    params.is_active = searchParams.is_active;
                }
            }

            const response = await api.get('/teams', { params });

            setTeams(response.data.data);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching teams:', error);
            toast.error('Error fetching teams');
        } finally {
            setLoading(false);
        }
    };

    // Fetch teams on mount and when dependencies change
    React.useEffect(() => {
        fetchTeams();
    }, [pagination.current_page, limit, searchParams?.is_active]);

    // Search in database when filtered list is empty but search params exist
    React.useEffect(() => {
        if (Object.keys(searchParams).length > 0 && filteredTeams.length === 0 && !loading) {
            fetchTeams(true);
        }
    }, [filteredTeams.length, searchParams]);

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams(prev => {
            const newParams = { ...prev };
            if (value) {
                newParams[column] = value;
            } else {
                delete newParams[column];
            }
            return newParams;
        });
        setPagination(prev => ({ ...prev, current_page: 1 }));

        // If search is cleared (empty value), fetch fresh data from API
        if (!value) {
            fetchTeams(false);
        }
    };

    // Get status style
    const getStatusStyle = (is_active: boolean) => {
        return is_active
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800';
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle create team
    const handleCreateTeam = () => {
        setSelectedTeam(null);
        setOffcanvasMode('create');
        setShowTeamOffcanvas(true);
    };

    // Handle edit team
    const handleEditTeam = (team: ITeam) => {
        setSelectedTeam(team);
        setOffcanvasMode('edit');
        setShowTeamOffcanvas(true);
    };

    // Handle delete
    const handleDeleteClick = (team: ITeam) => {
        setSelectedTeam(team);
        setDeleteConfirmOpen(true);
    };

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedTeam) {
            setDeleteLoading(true);
            try {
                await api.delete(`/teams/${selectedTeam.id}`);
                toast.success('Team deleted successfully!');
                await fetchTeams(); // Refresh the list
            } catch (err: any) {
                console.error('Error deleting team:', err);
                toast.error(err?.response?.data?.message || 'Failed to delete team');
            } finally {
                setDeleteLoading(false);
                setDeleteConfirmOpen(false);
                setSelectedTeam(null);
            }
        }
    };

    // Handle team offcanvas success
    const handleTeamOffcanvasSuccess = () => {
        fetchTeams(); // Refresh the teams list
    };

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Teams
                </h2>
                <Breadcrumb />
            </div>

            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <button
                        onClick={handleCreateTeam}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiUserPlus className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add Team</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Team Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Location
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Players Count
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Team Name"
                                        value={searchParams.name || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('name', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Location"
                                        value={searchParams.location || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('location', e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    {/* Empty cell for players count filter */}
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.is_active || ''}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange('is_active', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredTeams.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-8 text-gray-500">
                                        No teams found
                                    </td>
                                </tr>
                            ) : (
                                filteredTeams.map(team => (
                                    <tr key={team.id}
                                        onClick={() => navigate('/teams/view', { state: { team_id: team.id } })}
                                        className="hover:bg-gray-50 hover:cursor-pointer">
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex font-semibold items-center">
                                                {team.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            {team.location}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                                {team.players_count} Players
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(team.is_active)}`}>
                                                {team.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3">
                                                <Link
                                                    to='/teams/view'
                                                    state={{ team_id: team.id }}
                                                    className="text-primary-600 hover:text-primary-700"
                                                    title="View Team"
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </Link>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent navigation
                                                        handleEditTeam(team);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Edit Team"
                                                >
                                                    <FiEdit3 className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent navigation
                                                        handleDeleteClick(team);
                                                    }}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete Team"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                {/* Team Create/Edit Offcanvas */}
                <TeamOffcanvas
                    isOpen={showTeamOffcanvas}
                    onClose={() => setShowTeamOffcanvas(false)}
                    onSuccess={handleTeamOffcanvasSuccess}
                    team={selectedTeam}
                    mode={offcanvasMode}
                />

                {/* Delete Confirmation Modal */}
                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Team"
                        description="Are you sure you want to delete this team? This action cannot be undone."
                        loading={deleteLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default Teams;